import {
  addApolloState,
  initializeApollo,
  Helmet,
  renderMeta,
} from "@neonjungle/headless";
import { PageProps, PreviewDataType } from "@/lib/types";
import { GetStaticPaths, GetStaticProps } from "next";
import {
  AllPagesDocument,
  ContactPage,
  ContentPage,
  LandingPage,
  PageDocument,
  PageInterface,
  PageQueryVariables,
  SiteObjectType,
} from "graphql/generated-types";
import PageContainer from "@/layouts/PageContainer";
import PageRouter from "@/components/PageRouter";

export const getStaticPaths: GetStaticPaths = async () => {
  const apolloClient = initializeApollo();
  const { data } = await apolloClient.query({
    query: AllPagesDocument,
    variables: {
      hostname: process.env.HOSTNAME,
    },
  });

  const paths = data?.site?.pages
    .filter((p: PageInterface) => p.live)
    .map((page: PageInterface) => {
      return {
        params: {
          urlPaths: page.urlPath.split("/").filter((s: string, i: number) => {
            if (i > 1 && s) {
              return s;
            }
          }),
        },
      };
    })
    .filter(
      (p: { params: { urlPaths: string[] } }) => p.params.urlPaths.length > 0
    );

  return {
    paths: paths,
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const apolloClient = initializeApollo();
  const { previewData } = context as { previewData: PreviewDataType };

  let path = context.params?.urlPaths;
  path = `/${path instanceof Array ? path.join("/") : path}/`;

  const queryVariables: PageQueryVariables = {
    hostname: process.env.HOSTNAME!,
    urlPath: path,
    token: previewData?.previewToken,
    contentType: previewData?.contentType,
  };

  const { data } = await apolloClient.query({
    query: PageDocument,
    variables: queryVariables,
  });
  if (!data?.site?.page) {
    return {
      notFound: true,
    };
  }

  const pageProps = addApolloState(apolloClient, {
    props: {
      data,
    },
  }) as PageProps;

  pageProps.revalidate = 10;

  return pageProps;
};

export type CMSPage = ContentPage | LandingPage | ContactPage; // | NewListing | ProjectPage etc

type LocalSiteObjectType = SiteObjectType & {
  page: CMSPage;
};

type CMSPageProps = {
  site: LocalSiteObjectType;
};

const CMSPage = ({ data: { site } }: { data: CMSPageProps }) => {
  const page = site.page as CMSPage;
  return (
    <PageContainer pageType={page?.__typename!}>
      <Helmet>{renderMeta(page, site)}</Helmet>
      <PageRouter data={page} />
    </PageContainer>
  );
};

export default CMSPage;
